import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  YMaps,
  Map,
  Polyline,
  Placemark,
  Clusterer,
  ZoomControl,
  TypeSelector,
  FullscreenControl,
} from "@pbe/react-yandex-maps";
import { CButton } from "@coreui/react";
import {
  buildTrackIndex,
  positionAt,
  textColorOn,
  speedColor,
  percentile,
  SPEED_GRADIENT_CSS,
} from "../utils/truckActivity";
import { indexAt } from "./TripProgressStrip";

// Карта самосвала: трек, точки погрузки и разгрузки, текущее положение по времени кадра видео.
// Кладется в src/core/pages/CameraDetailPage/components/TruckMap.jsx
//
// - Экземпляр карты не пересоздается при смене рейса: тип тайлов, центр и масштаб остаются
//   пользовательскими. Они же сохраняются в viewState (ref со страницы), поэтому карта в окне
//   видео открывается там же, где пользователь оставил карту на странице.
// - Точки погрузки и разгрузки кластеризуются визуально: близкие на текущем масштабе точки
//   показываются одной иконкой с числом и разъединяются при приближении. Сами точки не
//   объединяются - разные рейсы могли ездить по одному маршруту.
// - Отметка положения живет в отдельном компоненте и сама подписана на время кадра:
//   смена кадра видео не перерисовывает карту.
// - «Скорость»: трек раскрашен по скорости, как пробки - от красного (медленно) к светло-зеленому
//   (быстро). Шкала - по треку, который сейчас на карте (весь период или один рейс):
//   минимум и максимум - 5-й и 95-й процентили скорости точек, чтобы выбросы не растягивали шкалу.

const LOAD_COLOR = "#e2572b";
const UNLOAD_COLOR = "#8e5cc9";
const POSITION_THROTTLE_MS = 250;
// После ручного сдвига или масштаба слежение ждет столько, потом возвращает машину в центр
const FOLLOW_PAUSE_MS = 10000;
// Действия пользователя на карте. Программные сдвиги (setCenter) этих событий не дают
const USER_ACTION_EVENTS = ["mousedown", "wheel", "multitouchstart"];

// Постоянные объекты опций: библиотека сравнивает их по ссылке и иначе переприменяет на каждом рендере
// suppressMapOpenBlock - убирает «Открыть в Яндекс Картах» и «Создать свою карту»;
// yandexMapDisablePoiInteractivity - не открывать карточки организаций по клику на подложку
// autoFitToViewport - карта сама подстраивается под изменение размеров контейнера
const MAP_OPTIONS = {
  suppressMapOpenBlock: true,
  yandexMapDisablePoiInteractivity: true,
  autoFitToViewport: "always",
};
const TRACK_OPTIONS = { strokeColor: "#0d6efd", strokeWidth: 4, strokeOpacity: 0.7 };
const POSITION_OPTIONS = { preset: "islands#redAutoIcon", zIndex: 1000 };
const HINT_MODULES = ["geoObject.addon.hint"];
const ZOOM_OPTIONS = { float: "right" };
const FIT_OPTIONS = { checkZoomRange: true, zoomMargin: 30 };
const CLUSTER_OPTIONS = {
  load: {
    preset: "islands#invertedOrangeClusterIcons",
    groupByCoordinates: false,
    gridSize: 64,
    hasBalloon: false,
    clusterDisableClickZoom: false,
  },
  unload: {
    preset: "islands#invertedVioletClusterIcons",
    groupByCoordinates: false,
    gridSize: 64,
    hasBalloon: false,
    clusterDisableClickZoom: false,
  },
};
const POINT_OPTIONS = {
  load: { preset: "islands#orangeCircleDotIcon" },
  unload: { preset: "islands#violetCircleDotIcon" },
};

// Раскраска по скорости: столько оттенков; участок короче SPEED_MIN_RUN шагов перекрашивается
// в цвет соседа - иначе дрожание скорости дробит трек на тысячи кусочков и карта тормозит
const SPEED_BINS = 10;
const SPEED_MIN_RUN = 3;
// Шаг трека длиннее этого - дыра в записи GPS, линию через нее не рисуем
const SPEED_MAX_STEP_MS = 60000;
const NO_SPEED_COLOR = "#9e9e9e";

// Участки трека одного цвета: [{ key, coords, color, hint }]
const buildSpeedRuns = (track, lo, hi) => {
  const n = track.length;
  if (n < 2 || lo == null || hi == null) return [];
  const span = Math.max(1, hi - lo);
  const steps = [];
  for (let i = 0; i < n - 1; i += 1) {
    const a = track[i];
    const b = track[i + 1];
    const dt = new Date(b.ts).getTime() - new Date(a.ts).getTime();
    if (!(dt > 0) || dt > SPEED_MAX_STEP_MS) {
      steps.push(null);
      continue;
    }
    const va = a.speed_kmh;
    const vb = b.speed_kmh;
    const v = va != null && vb != null ? (va + vb) / 2 : va ?? vb;
    steps.push(v == null ? -1 : Math.round(((Math.min(hi, Math.max(lo, v)) - lo) / span) * (SPEED_BINS - 1)));
  }

  // короткие участки - в цвет предыдущего
  let i = 0;
  while (i < steps.length) {
    let j = i;
    while (j + 1 < steps.length && steps[j + 1] === steps[i]) j += 1;
    if (steps[i] !== null && j - i + 1 < SPEED_MIN_RUN && i > 0 && steps[i - 1] !== null) {
      for (let k = i; k <= j; k += 1) steps[k] = steps[i - 1];
    }
    i = j + 1;
  }

  const runs = [];
  let cur = null;
  steps.forEach((bin, idx) => {
    if (bin === null) {
      cur = null;
      return;
    }
    if (!cur || cur.bin !== bin) {
      const vFrom = lo + (span * Math.max(0, bin - 0.5)) / (SPEED_BINS - 1);
      const vTo = lo + (span * Math.min(SPEED_BINS - 1, bin + 0.5)) / (SPEED_BINS - 1);
      cur = {
        key: `${idx}`,
        bin,
        coords: [[track[idx].lat, track[idx].lon]],
        color: bin < 0 ? NO_SPEED_COLOR : speedColor(bin / (SPEED_BINS - 1)),
        hint: bin < 0 ? "Скорость неизвестна" : `≈ ${Math.round(vFrom)}–${Math.round(vTo)} км/ч`,
      };
      runs.push(cur);
    }
    cur.coords.push([track[idx + 1].lat, track[idx + 1].lon]);
  });
  return runs;
};

// Участок трека; опции и свойства - постоянные объекты на участок, чтобы не переприменять их
const SpeedRun = React.memo(function SpeedRun({ run }) {
  const options = useMemo(() => ({ strokeColor: run.color, strokeWidth: 5, strokeOpacity: 0.9 }), [run.color]);
  const properties = useMemo(() => ({ hintContent: run.hint }), [run.hint]);
  return <Polyline geometry={run.coords} options={options} properties={properties} modules={HINT_MODULES} />;
});

const computeBounds = (coords) => {
  if (!coords.length) return null;
  let minLat = Infinity;
  let minLon = Infinity;
  let maxLat = -Infinity;
  let maxLon = -Infinity;
  coords.forEach(([lat, lon]) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  });
  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
};

const LegendDot = ({ color, label }) => (
  <span className="d-inline-flex align-items-center me-3">
    <span
      className="d-inline-block rounded-circle me-1"
      style={{ width: "10px", height: "10px", backgroundColor: color }}
    />
    {label}
  </span>
);

// Отметка положения машины в текущем кадре видео. Подписана на хранилище сама,
// перерисовывается не чаще раза в 250 мс и не трогает остальную карту.
// «Следить»: машина держится в центре. Ручной сдвиг или масштаб приостанавливают
// слежение на 10 секунд, потом карта возвращается к машине - как в навигаторе.
const PositionMarker = ({ playheadStore, trackIndex, follow, map, onPausedChange }) => {
  const [ms, setMs] = useState(() => playheadStore.get());
  const pausedUntilRef = useRef(0);
  const positionRef = useRef(null);

  useEffect(() => {
    let last = 0;
    let timer = null;
    const apply = () => {
      timer = null;
      last = performance.now();
      setMs(playheadStore.get());
    };
    const unsubscribe = playheadStore.subscribe(() => {
      const elapsed = performance.now() - last;
      if (elapsed >= POSITION_THROTTLE_MS) apply();
      else if (!timer) timer = setTimeout(apply, POSITION_THROTTLE_MS - elapsed);
    });
    apply();
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [playheadStore]);

  const position = useMemo(() => (ms == null ? null : positionAt(trackIndex, ms)), [ms, trackIndex]);
  positionRef.current = position;

  const recenter = useCallback(() => {
    const pos = positionRef.current;
    if (map && pos) map.setCenter(pos, map.getZoom(), { duration: 200 });
  }, [map]);

  // Действия пользователя ставят слежение на паузу; через 10 с без действий - снова в центр
  useEffect(() => {
    if (!follow || !map) return undefined;
    let timer = null;
    const onUserAction = () => {
      pausedUntilRef.current = performance.now() + FOLLOW_PAUSE_MS;
      onPausedChange(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        pausedUntilRef.current = 0;
        onPausedChange(false);
        recenter();
      }, FOLLOW_PAUSE_MS);
    };
    map.events.add(USER_ACTION_EVENTS, onUserAction);
    recenter();
    return () => {
      map.events.remove(USER_ACTION_EVENTS, onUserAction);
      if (timer) clearTimeout(timer);
      pausedUntilRef.current = 0;
      onPausedChange(false);
    };
  }, [follow, map, recenter, onPausedChange]);

  // Машина сдвинулась - держим ее в центре, если слежение не на паузе
  useEffect(() => {
    if (!follow || !map || !position) return;
    if (performance.now() < pausedUntilRef.current) return;
    map.setCenter(position, map.getZoom(), { duration: 200 });
  }, [follow, map, position]);

  if (!position) return null;
  return <Placemark geometry={position} options={POSITION_OPTIONS} />;
};

const isOverlayItem = (item) => item.originalItem?.class_id === -2 || item.originalItem?.class_id === -3;

// Статус машины в кадре видео: плашка в цвет сегмента активности под кадром.
// Цвет текста - по яркости фона (textColorOn), светлый текст дополнительно с тенью.
// Обновляется напрямую через DOM по времени кадра - без рендера карты и страницы.
const STATUS_EMPTY_BG = "#e9ecef";

const PlaybackStatus = ({ playheadStore, activities }) => {
  const badgeRef = useRef(null);
  const lastRef = useRef(undefined);

  const segments = useMemo(
    () => activities.filter((it) => !isOverlayItem(it)).sort((a, b) => a.start - b.start),
    [activities]
  );

  const update = useCallback(() => {
    const el = badgeRef.current;
    if (!el) return;
    const ms = playheadStore.get();
    const seg = ms == null ? null : segments[indexAt(segments, ms)] || null;
    const key = seg ? seg.id : null;
    if (key === lastRef.current) return;
    lastRef.current = key;

    const bg = seg ? seg.color : STATUS_EMPTY_BG;
    const fg = seg ? textColorOn(bg) : "#6c757d";
    el.textContent = seg ? seg.label : "—";
    el.style.backgroundColor = bg;
    el.style.color = fg;
    el.style.textShadow = fg === "#fff" ? "0 1px 2px rgba(0, 0, 0, 0.45)" : "none";
    el.style.boxShadow = seg ? `0 2px 8px ${bg}66, inset 0 0 0 1px rgba(0, 0, 0, 0.12)` : "none";
  }, [playheadStore, segments]);

  useEffect(() => {
    lastRef.current = undefined;
    update();
    return playheadStore.subscribe(update);
  }, [playheadStore, update]);

  return (
    <div className="d-flex align-items-center flex-shrink-0 gap-2">
      <span className="text-muted">Статус:</span>
      <span
        ref={badgeRef}
        className="fw-bold text-nowrap"
        style={{
          padding: "3px 12px",
          borderRadius: "999px",
          fontSize: "0.9rem",
          letterSpacing: "0.01em",
          backgroundColor: STATUS_EMPTY_BG,
          color: "#6c757d",
          transition: "background-color 0.2s, color 0.2s, box-shadow 0.2s",
        }}
      >
        —
      </span>
    </div>
  );
};

PlaybackStatus.propTypes = {
  playheadStore: PropTypes.object.isRequired,
  activities: PropTypes.array.isRequired,
};

// Точки не перерисовываются, пока не изменился их список
const PointsLayer = React.memo(function PointsLayer({ points, kind, onPointClick }) {
  return (
    <Clusterer options={CLUSTER_OPTIONS[kind]}>
      {points.map((p) => (
        <Placemark
          key={p.id}
          geometry={p.coords}
          modules={HINT_MODULES}
          properties={{ hintContent: p.hint }}
          options={POINT_OPTIONS[kind]}
          onClick={() => onPointClick && onPointClick({ exactTime: p.timeMs, item: { label: p.hint } })}
        />
      ))}
    </Clusterer>
  );
});

const TruckMap = ({
  track,
  loadPoints,
  unloadPoints,
  height,
  viewState,
  playheadStore,
  onPointClick,
  caption,
  onShowAll,
  autoTrip,
  onAutoTripChange,
  fill,
  activities,
}) => {
  const mapRef = useRef(null);
  // Экземпляр карты в состоянии нужен отметке положения: она подписывается на его события
  const [mapInstance, setMapInstance] = useState(null);
  const [follow, setFollow] = useState(false);
  const [followPaused, setFollowPaused] = useState(false);
  const [speedMode, setSpeedMode] = useState(true);

  const coords = useMemo(() => track.map((p) => [p.lat, p.lon]), [track]);
  const bounds = useMemo(() => computeBounds(coords), [coords]);
  const trackIndex = useMemo(() => buildTrackIndex(track), [track]);

  // Шкала скорости - по треку, который сейчас на карте (весь период или выбранный рейс)
  const speedStats = useMemo(() => {
    const values = track.map((p) => p.speed_kmh).filter((v) => v != null && Number.isFinite(v));
    if (!values.length) return null;
    const lo = percentile(values, 5);
    const hi = percentile(values, 95);
    return {
      lo,
      hi: hi > lo ? hi : lo + 1,
      avg: values.reduce((acc, v) => acc + v, 0) / values.length,
    };
  }, [track]);
  const showSpeed = speedMode && speedStats !== null;
  const speedRuns = useMemo(
    () => (showSpeed ? buildSpeedRuns(track, speedStats.lo, speedStats.hi) : []),
    [showSpeed, track, speedStats]
  );

  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  // Начальное состояние - только при создании карты
  const [defaultState] = useState(() => ({ center: coords[0] || [55.75, 37.62], zoom: 14, controls: [] }));

  // Колбэк экземпляра стабилен: иначе библиотека вызывает его на каждом рендере, и вид
  // пользователя откатывался бы к сохраненному. Каждый экземпляр настраиваем ровно один раз.
  const initializedRef = useRef(new WeakSet());
  const handleInstance = useCallback((map) => {
    if (!map) {
      mapRef.current = null;
      setMapInstance(null);
      return;
    }
    mapRef.current = map;
    if (initializedRef.current.has(map)) return;
    initializedRef.current.add(map);
    setMapInstance(map);

    const store = viewStateRef.current;
    const saved = store ? store.current : null;
    if (saved && saved.center) {
      map.setCenter(saved.center, saved.zoom);
    } else if (boundsRef.current) {
      map.setBounds(boundsRef.current, FIT_OPTIONS);
    }
    if (saved && saved.type) map.setType(saved.type);

    if (store) {
      map.events.add("boundschange", () => {
        store.current = { ...store.current, center: map.getCenter(), zoom: map.getZoom() };
      });
      map.events.add("typechange", () => {
        store.current = { ...store.current, type: map.getType() };
      });
    }
  }, []);

  // Трек пришел позже создания карты, а вида пользователя еще нет - вписываем один раз
  useEffect(() => {
    const map = mapRef.current;
    const saved = viewStateRef.current ? viewStateRef.current.current : null;
    if (!map || !bounds || (saved && saved.center)) return;
    map.setBounds(bounds, FIT_OPTIONS);
  }, [bounds]);

  const fitTrack = () => {
    if (mapRef.current && bounds) mapRef.current.setBounds(bounds, FIT_OPTIONS);
  };

  // fill: карта занимает всю ширину и высоту родителя (родитель должен иметь высоту)
  return (
    <div style={fill ? { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 } : undefined}>
      {/* Одна строка над картой (на невысоком экране каждая строка на счету). В окне видео:
          статус машины в кадре, легенда, кнопки - переносятся, если не помещаются.
          На странице: легенда слева, кнопки справа */}
      <div
        className={`d-flex flex-wrap align-items-center gap-2 mb-2 small flex-shrink-0 ${
          playheadStore ? "" : "justify-content-between"
        }`}
      >
        {playheadStore && <PlaybackStatus playheadStore={playheadStore} activities={activities} />}
        <div className="text-muted d-flex flex-wrap align-items-center">
          <LegendDot color={LOAD_COLOR} label={`Погрузки: ${loadPoints.length}`} />
          <LegendDot color={UNLOAD_COLOR} label={`Разгрузки: ${unloadPoints.length}`} />
        </div>
        <div className={`d-flex flex-wrap align-items-center gap-2${playheadStore ? " ms-auto" : ""}`}>
          {caption && <span className="fw-semibold text-dark">{caption}</span>}
          {onShowAll && (
            <CButton size="sm" color="secondary" variant="outline" onClick={onShowAll}>
              Весь период
            </CButton>
          )}
          {onAutoTripChange && (
            <div className="form-check form-switch mb-0 ps-0 d-flex align-items-center gap-2" title="Показывать на карте рейс, в котором сейчас находится видео">
              <input
                className="form-check-input float-none m-0"
                type="checkbox"
                id="truckMapAutoTrip"
                checked={Boolean(autoTrip)}
                onChange={(e) => onAutoTripChange(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="truckMapAutoTrip">
                Рейс по видео
              </label>
            </div>
          )}
          {playheadStore && (
            <div className="form-check form-switch mb-0 ps-0 d-flex align-items-center gap-2" title="Держать машину в центре карты">
              <input
                className="form-check-input float-none m-0"
                type="checkbox"
                id="truckMapFollow"
                checked={follow}
                onChange={(e) => setFollow(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="truckMapFollow">
                Следить{follow && followPaused && <span className="text-muted"> (пауза 10 с)</span>}
              </label>
            </div>
          )}
          {speedStats && (
            <div
              className="form-check form-switch mb-0 ps-0 d-flex align-items-center gap-2"
              title="Раскрасить трек по скорости: красный - медленно, зеленый - быстро. Шкала - по треку на карте, без выбросов (5-й и 95-й процентили)"
            >
              <input
                className="form-check-input float-none m-0"
                type="checkbox"
                id={`truckMapSpeed${playheadStore ? "Video" : ""}`}
                checked={speedMode}
                onChange={(e) => setSpeedMode(e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`truckMapSpeed${playheadStore ? "Video" : ""}`}>
                Скорость
              </label>
            </div>
          )}
          <CButton size="sm" color="primary" variant="outline" onClick={fitTrack} disabled={!bounds}>
            Вписать трек
          </CButton>
        </div>
      </div>

      <div
        className="position-relative"
        style={{
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #ccc",
          ...(fill ? { flex: "1 1 auto", minHeight: 0 } : {}),
        }}
      >
        <YMaps query={{ lang: "ru_RU" }}>
          <Map
            instanceRef={handleInstance}
            defaultState={defaultState}
            options={MAP_OPTIONS}
            width="100%"
            height={fill ? "100%" : height}
          >
            {coords.length > 1 && !showSpeed && <Polyline geometry={coords} options={TRACK_OPTIONS} />}
            {speedRuns.map((run) => (
              <SpeedRun key={run.key} run={run} />
            ))}

            <PointsLayer points={loadPoints} kind="load" onPointClick={onPointClick} />
            <PointsLayer points={unloadPoints} kind="unload" onPointClick={onPointClick} />

            {playheadStore && (
              <PositionMarker
                playheadStore={playheadStore}
                trackIndex={trackIndex}
                follow={follow}
                map={mapInstance}
                onPausedChange={setFollowPaused}
              />
            )}

            <ZoomControl options={ZOOM_OPTIONS} />
            <TypeSelector options={ZOOM_OPTIONS} />
            <FullscreenControl />
          </Map>
        </YMaps>

        {showSpeed && (
          <div
            className="position-absolute bg-white rounded shadow-sm px-2 py-1 small"
            style={{ left: "8px", bottom: "8px", zIndex: 2, pointerEvents: "none", minWidth: "190px" }}
          >
            <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.7rem" }}>
              <span>мин</span>
              <span>сред</span>
              <span>макс</span>
            </div>
            <div style={{ height: "8px", borderRadius: "4px", background: SPEED_GRADIENT_CSS }} />
            <div className="d-flex justify-content-between fw-semibold" style={{ fontSize: "0.75rem" }}>
              <span>{Math.round(speedStats.lo)}</span>
              <span>{Math.round(speedStats.avg)}</span>
              <span>{Math.round(speedStats.hi)} км/ч</span>
            </div>
          </div>
        )}

        {coords.length === 0 && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-muted"
            style={{ background: "rgba(245, 245, 245, 0.85)", pointerEvents: "none" }}
          >
            Нет данных GPS за выбранный период или рейс
          </div>
        )}
      </div>
    </div>
  );
};

TruckMap.propTypes = {
  track: PropTypes.array,
  loadPoints: PropTypes.array,
  unloadPoints: PropTypes.array,
  height: PropTypes.string,
  viewState: PropTypes.shape({ current: PropTypes.object }),
  playheadStore: PropTypes.object,
  onPointClick: PropTypes.func,
  caption: PropTypes.string,
  onShowAll: PropTypes.func,
  autoTrip: PropTypes.bool,
  onAutoTripChange: PropTypes.func,
  fill: PropTypes.bool,
  activities: PropTypes.array,
};

TruckMap.defaultProps = {
  track: [],
  loadPoints: [],
  unloadPoints: [],
  height: "500px",
  viewState: null,
  playheadStore: null,
  onPointClick: null,
  caption: "",
  onShowAll: null,
  autoTrip: false,
  onAutoTripChange: null,
  fill: false,
  activities: [],
};

export default React.memo(TruckMap);
